
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Search, Eye, CheckCircle, DollarSign } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import PaymentStatusBadge from '@/components/PaymentStatusBadge'

const AdminUsers = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const { toast } = useToast()

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          schools (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      console.log('Fetched users:', data?.length || 0)
      console.log('Users data:', data)

      if (error) {
        console.error('Error fetching users:', error)
        toast({
          title: "Error fetching users",
          description: error.message,
          variant: "destructive",
        })
      } else {
        console.log('Setting users:', data?.length || 0)
        setUsers(data || [])
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsPaid = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          payment_status: 'paid',
          payment_completed_at: new Date().toISOString()
        })
        .eq('id', userId)
      
      if (error) throw error
      
      toast({
        title: "Payment status updated",
        description: "User has been marked as paid.",
      })
      
      // Refresh the user list
      fetchUsers()
    } catch (error: any) {
      toast({
        title: "Error updating payment status",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleMarkCodeAsEntered = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          has_entered_unlock_code: true
        })
        .eq('id', userId)
      
      if (error) throw error
      
      toast({
        title: "Unlock code marked as entered",
        description: "User can now access the platform.",
      })
      
      // Refresh the user list
      fetchUsers()
    } catch (error: any) {
      toast({
        title: "Error updating unlock code status",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.schools?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Users Management</CardTitle>
          <CardDescription>Loading users...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Users Management</CardTitle>
            <CardDescription>View and manage registered users</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 w-64"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>School</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Points</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead>Code Entered</TableHead>
              <TableHead>Unlock Code</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">
                  {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                </TableCell>
                <TableCell>{user.email || 'N/A'}</TableCell>
                <TableCell>{user.schools?.name || 'N/A'}</TableCell>
                <TableCell>{user.class || 'N/A'}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{user.level || 1}</Badge>
                </TableCell>
                <TableCell>{user.points || 0}</TableCell>
                <TableCell>
                  <PaymentStatusBadge status={user.payment_status || 'free'} />
                </TableCell>
                <TableCell>
                  {user.has_entered_unlock_code ? (
                    <Badge className="bg-green-100 text-green-700">Yes</Badge>
                  ) : (
                    <Badge variant="outline">No</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {user.assigned_unlock_code ? (
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {user.assigned_unlock_code}
                    </code>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </TableCell>
                <TableCell>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // TODO: Implement user detail view
                        toast({
                          title: "Feature coming soon",
                          description: "User detail view will be implemented",
                        })
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {user.payment_status === 'pending' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleMarkAsPaid(user.id)}
                        className="flex items-center gap-1"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Mark Paid
                      </Button>
                    )}
                    {user.payment_status === 'paid' && !user.has_entered_unlock_code && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleMarkCodeAsEntered(user.id)}
                        className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve Code
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'No users found matching your search.' : 'No users registered yet.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

export default AdminUsers
